import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { invalidateShared, SHARED_KEYS } from '@shared/services/sharedResource';
import { mfaService, MFAStatusResponse } from '@shared/services/mfa';
import { useUser } from '@shared/hooks/useUser';
import { Card, Badge, Button, Input } from '@shared/components/ui';
import {
  User,
  Mail,
  Globe,
  Languages,
  ShieldCheck,
  Shield,
  Clock,
  Calendar,
  Lock,
  Download,
  FileText,
  Trash2,
  AlertTriangle,
  ChevronRight,
  CheckCircle2,
  KeyRound,
} from 'lucide-react';
import { getRoleTone } from '@shared/config/theme';
import { StatusChip } from '@shared/components/StatusChip';
import { normalizeProfileLanguage, PROFILE_LANGUAGES } from '../utils/profileLanguage';

const TIMEZONES = [
  'UTC',
  'America/Noronha',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Araguaina',
  'America/Maceio',
  'America/Bahia',
  'America/Sao_Paulo',
  'America/Campo_Grande',
  'America/Cuiaba',
  'America/Porto_Velho',
  'America/Boa_Vista',
  'America/Manaus',
  'America/Eirunepe',
  'America/Rio_Branco',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'America/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Vienna',
  'Europe/Zurich',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Copenhagen',
  'Europe/Helsinki',
  'Europe/Warsaw',
  'Europe/Prague',
  'Europe/Budapest',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Europe/Lisbon',
  'America/Buenos_Aires',
  'America/Santiago',
  'America/Lima',
  'America/Bogota',
  'America/Caracas',
  'America/Montevideo',
  'America/Asuncion',
  'America/Quito',
  'America/La_Paz',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Seoul',
  'Asia/Taipei',
  'Asia/Bangkok',
  'Asia/Jakarta',
  'Asia/Manila',
  'Asia/Kuala_Lumpur',
  'Asia/Ho_Chi_Minh',
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Kolkata',
  'Asia/Mumbai',
  'Asia/Karachi',
  'Asia/Dhaka',
  'Asia/Tehran',
  'Asia/Jerusalem',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Darwin',
  'Australia/Hobart',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'Pacific/Honolulu',
  'Africa/Cairo',
  'Africa/Lagos',
  'Africa/Johannesburg',
  'Africa/Nairobi',
  'Africa/Casablanca',
  'Africa/Algiers',
  'Africa/Tunis',
  'Africa/Accra',
  'Africa/Addis_Ababa',
].sort();
type UserWithMeta = {
  created_at?: string;
  last_login_at?: string;
};

function formatRelativeTime(dateStr: string | undefined, locale: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (diffMins < 1) return relative.format(0, 'second');
  if (diffHours < 1) return relative.format(-diffMins, 'minute');
  if (diffDays < 1) return relative.format(-diffHours, 'hour');
  if (diffDays < 30) return relative.format(-diffDays, 'day');
  return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

function formatMemberSince(dateStr: string | undefined, locale: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

// Role hue comes from the shared contract: settings/team-members, profile and
// organizations render the same role with the same color.

export default function Profile() {
  const { t, i18n } = useTranslation('settings');
  const locale = i18n.resolvedLanguage || i18n.language;
  const navigate = useNavigate();
  const {
    user: hookUser,
    error: profileLoadError,
    retry: retryProfile,
    loading: profileLoading,
  } = useUser();
  const user = hookUser as NonNullable<typeof hookUser> & UserWithMeta;
  const [saving, setSaving] = useState(false);
  const [fullName, setFullName] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [language, setLanguage] = useState('en');
  const [savedProfile, setSavedProfile] = useState({
    fullName: '',
    timezone: 'UTC',
    language: 'en',
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [mfaStatus, setMfaStatus] = useState<MFAStatusResponse | null>(null);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [mfaLoadError, setMfaLoadError] = useState(false);
  const [showDisableMfa, setShowDisableMfa] = useState(false);
  const [disableMfaCode, setDisableMfaCode] = useState('');
  const [disableMfaLoading, setDisableMfaLoading] = useState(false);
  const mfaDialogRef = useRef<HTMLDivElement>(null);
  const mfaPendingRef = useRef(false);
  mfaPendingRef.current = disableMfaLoading;
  useEffect(() => {
    if (!showDisableMfa) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = mfaDialogRef.current;
    dialog?.querySelector('input')?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!mfaPendingRef.current) {
          setError('');
          setShowDisableMfa(false);
        }
      }
      if (event.key !== 'Tab' || !dialog) return;
      const controls = Array.from(dialog.querySelectorAll<HTMLElement>('input,button')).filter(
        (el) => !el.matches(':disabled')
      );
      const first = controls[0],
        last = controls[controls.length - 1];
      if (!first) {
        event.preventDefault();
        dialog.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (
        (!event.shiftKey && document.activeElement === last) ||
        !dialog.contains(document.activeElement)
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      opener?.focus();
    };
  }, [showDisableMfa]);
  const hasProfileChanges = Boolean(
    hookUser &&
    (fullName !== savedProfile.fullName ||
      timezone !== savedProfile.timezone ||
      language !== savedProfile.language)
  );

  useEffect(() => {
    const syncLanguagePreference = (locale: string) =>
      setLanguage(normalizeProfileLanguage(locale));

    syncLanguagePreference(i18n.resolvedLanguage || i18n.language);
    i18n.on('languageChanged', syncLanguagePreference);
    return () => i18n.off('languageChanged', syncLanguagePreference);
  }, [i18n]);

  useEffect(() => {
    if (hookUser && !profileLoadError) {
      setFullName(hookUser.full_name || '');
      setTimezone(hookUser.timezone || 'UTC');
      setLanguage(normalizeProfileLanguage(hookUser.language));
      setSavedProfile({
        fullName: hookUser.full_name || '',
        timezone: hookUser.timezone || 'UTC',
        language: normalizeProfileLanguage(hookUser.language),
      });
      fetchMFAStatus();
    }
  }, [hookUser, profileLoadError]);

  const fetchMFAStatus = async () => {
    try {
      setMfaLoading(true);
      setMfaLoadError(false);
      const res = await mfaService.getMFAStatus();
      setMfaStatus(res);
    } catch {
      setMfaStatus(null);
      setMfaLoadError(true);
    } finally {
      setMfaLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      await api.put('/auth/profile', { full_name: fullName, timezone, language });
      setSavedProfile({ fullName, timezone, language });
      // Drop the shared profile cache so other components see the update now.
      invalidateShared(SHARED_KEYS.profile);
      await i18n.changeLanguage(language).catch(() => undefined);
      setSuccessMessage(t('profile.success'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch {
      setError(t('profile.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDisableMFA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disableMfaLoading || disableMfaCode.length !== 6) return;
    setError('');
    setDisableMfaLoading(true);
    try {
      await mfaService.disableMFA({ totp_code: disableMfaCode });
      setMfaStatus({ mfa_enabled: false });
      setShowDisableMfa(false);
      setDisableMfaCode('');
      setSuccessMessage(t('profile.mfa.disabled'));
    } catch {
      setError(t('profile.mfa.invalidCode'));
    } finally {
      setDisableMfaLoading(false);
    }
  };

  if (profileLoadError && !profileLoading) {
    return (
      <div
        className="mx-auto max-w-2xl rounded-xl border border-surface-border-light bg-surface-card-light p-6 dark:border-surface-border-dark dark:bg-surface-card-dark"
        role="alert"
      >
        <p className="mb-4 text-state-danger-light dark:text-state-danger-dark">
          {t('profile.loadError')}
        </p>
        <Button onClick={retryProfile}>{t('common:actions.retry')}</Button>
      </div>
    );
  }
  if (!hookUser || profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  const initials = savedProfile.fullName
    ? savedProfile.fullName
        .split(' ')
        .filter(Boolean)
        .map((n) => n.charAt(0).toUpperCase())
        .join('')
        .slice(0, 2)
    : '?';

  const translateRole = (role?: string) => {
    if (!role) return t('profile.standardUser');

    const normalizedRole = role.toLowerCase();
    if (normalizedRole === 'platform_admin') {
      return t('admin:platform_administrator', { defaultValue: role });
    }

    return t(`team.roles.${normalizedRole}`, { defaultValue: role });
  };

  return (
    <>
      <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-300">
        {/* Success Message */}
        {error && !showDisableMfa && (
          <div
            role="alert"
            className="rounded-xl border border-state-danger-light/20 bg-state-danger-bg-light p-4 text-state-danger-light dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
          >
            {error}
          </div>
        )}
        {successMessage && (
          <div className="bg-state-ok-bg-light dark:bg-state-ok-bg-dark border border-state-ok-light/20 dark:border-state-ok-dark/20 p-4 rounded-xl flex items-center gap-3 text-state-ok-light dark:text-state-ok-dark font-bold animate-in slide-in-from-top duration-300">
            <CheckCircle2 size={20} className="text-state-ok-light dark:text-state-ok-dark" />{' '}
            {successMessage}
          </div>
        )}

        {/* Section 1: Profile Hero */}
        <div className="flex items-center gap-6 p-6 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl">
          <div className="w-20 h-20 rounded-full bg-brand-600 flex items-center justify-center shadow-lg shrink-0">
            <span className="text-3xl font-black text-white">{initials}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-1">
              <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                {savedProfile.fullName}
              </h1>
              <StatusChip
                tone={getRoleTone(hookUser.role)}
                label={translateRole(hookUser.role)}
                size="md"
              />
            </div>
            <p className="text-sm font-medium text-text-muted-light dark:text-text-muted-dark">
              {user.created_at
                ? `${t('profile.memberSince')} ${formatMemberSince(user.created_at, locale)}`
                : ''}
            </p>
          </div>
        </div>

        {/* Section 2: Two-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Identity Card */}
              <Card className="p-8">
                <h2 className="text-xl font-black text-slate-900 dark:text-white mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center">
                    <User className="text-brand-600 dark:text-brand-400" size={20} />
                  </div>
                  {t('profile.identity')}
                </h2>
                <div className="space-y-6">
                  {/* Full name with User icon prefix */}
                  <div>
                    <label
                      htmlFor="profile-full-name"
                      className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 ml-1"
                    >
                      {t('profile.fullName')}
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark pointer-events-none">
                        <User size={16} />
                      </div>
                      <input
                        id="profile-full-name"
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                        className="w-full pl-10 pr-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-slate-900 dark:text-slate-100 text-sm font-medium transition-all placeholder:text-text-subtle-light dark:placeholder:text-text-subtle-dark focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Visual divider */}
                  <div className="border-t border-surface-border-light dark:border-surface-border-dark" />

                  {/* Email with Mail icon prefix */}
                  <div>
                    <label
                      htmlFor="profile-email"
                      className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 ml-1"
                    >
                      {t('profile.email')}
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark pointer-events-none">
                        <Mail size={16} />
                      </div>
                      <input
                        id="profile-email"
                        type="email"
                        value={hookUser.email}
                        readOnly
                        disabled
                        className="w-full pl-10 pr-4 py-3 bg-surface-2-light dark:bg-surface-2-dark opacity-60 border border-surface-border-light dark:border-surface-border-dark rounded-xl text-slate-900 dark:text-slate-100 text-sm font-medium transition-all focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Save button in card footer */}
                <div className="pt-6 mt-6 border-t border-surface-border-light dark:border-surface-border-dark flex justify-end">
                  <Button type="submit" isLoading={saving} disabled={saving || !hasProfileChanges}>
                    {t('profile.saveChanges')}
                  </Button>
                </div>
              </Card>

              {/* Preferences Card */}
              <Card className="p-8">
                <h2 className="text-xl font-black text-slate-900 dark:text-white mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center">
                    <Globe className="text-brand-600 dark:text-brand-400" size={20} />
                  </div>
                  {t('profile.preferences')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Timezone select with Globe icon */}
                  <div>
                    <label
                      htmlFor="profile-timezone"
                      className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 ml-1"
                    >
                      {t('profile.timezone')}
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark pointer-events-none">
                        <Globe size={16} />
                      </div>
                      <select
                        id="profile-timezone"
                        value={timezone}
                        onChange={(e) => setTimezone(e.target.value)}
                        className="w-full pl-10 pr-8 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none appearance-none"
                      >
                        {TIMEZONES.map((tz) => (
                          <option key={tz} value={tz}>
                            {tz}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Language select with Languages icon */}
                  <div>
                    <label
                      htmlFor="profile-language"
                      className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 ml-1"
                    >
                      {t('profile.language')}
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark pointer-events-none">
                        <Languages size={16} />
                      </div>
                      <select
                        id="profile-language"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        className="w-full pl-10 pr-8 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none appearance-none"
                      >
                        {PROFILE_LANGUAGES.map((l) => (
                          <option key={l.code} value={l.code}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </Card>
            </form>

            {/* Security Card */}
            <Card className="p-8">
              <h2 className="text-xl font-black text-slate-900 dark:text-white mb-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center">
                  <ShieldCheck className="text-state-ok-light dark:text-state-ok-dark" size={20} />
                </div>
                {t('profile.security')}
              </h2>

              <div className="space-y-6">
                {/* MFA Status */}
                {mfaLoading ? (
                  <p role="status">{t('common:labels.loading')}</p>
                ) : mfaLoadError ? (
                  <div
                    role="alert"
                    className="rounded-xl bg-state-danger-bg-light p-4 text-state-danger-light dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
                  >
                    <p>{t('profile.mfa.loadError')}</p>
                    <Button className="mt-3" onClick={fetchMFAStatus}>
                      {t('common:actions.retry')}
                    </Button>
                  </div>
                ) : (
                  <div className="p-6 bg-surface-2-light dark:bg-surface-2-dark rounded-2xl border border-surface-border-light dark:border-surface-border-dark flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-slate-900 dark:text-white">
                          {t('profile.mfa.title')}
                        </h3>
                        {mfaStatus?.mfa_enabled ? (
                          <Badge variant="success" className="flex items-center gap-1">
                            <ShieldCheck size={12} /> {t('profile.mfa.active')}
                          </Badge>
                        ) : (
                          <Badge variant="neutral" className="flex items-center gap-1">
                            <Shield size={12} /> {t('profile.mfa.inactive')}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                        {t('profile.mfa.description')}
                      </p>
                    </div>
                    {mfaStatus?.mfa_enabled ? (
                      <Button
                        variant="outline"
                        className="text-state-danger-light dark:text-state-danger-dark border-state-danger-light/20 dark:border-state-danger-dark/20 hover:bg-state-danger-bg-light dark:hover:bg-state-danger-bg-dark"
                        onClick={() => {
                          setError('');
                          setShowDisableMfa(true);
                        }}
                      >
                        {t('profile.mfa.disable')}
                      </Button>
                    ) : (
                      <Button onClick={() => navigate('/mfa/setup')}>
                        {t('profile.mfa.enable')}
                      </Button>
                    )}
                  </div>
                )}

                {/* Last login */}
                {user.last_login_at && (
                  <div className="flex items-center gap-3 text-sm text-text-muted-light dark:text-text-muted-dark">
                    <Clock
                      size={16}
                      className="text-text-subtle-light dark:text-text-subtle-dark"
                    />
                    <span>
                      {t('profile.lastLogin', {
                        relativeTime: formatRelativeTime(user.last_login_at, locale),
                      })}
                    </span>
                  </div>
                )}

                {/* Change password link */}
                <Button
                  variant="outline"
                  onClick={() => navigate('/change-password')}
                  className="w-full md:w-auto"
                >
                  <KeyRound size={16} className="mr-2" />
                  {t('profile.changePassword')}
                </Button>
              </div>
            </Card>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-1 space-y-8">
            {/* Account Status Card */}
            <Card className="p-6 bg-brand-600 text-white overflow-hidden relative">
              <h3 className="text-sm font-black uppercase tracking-widest text-brand-100 mb-4">
                {t('profile.accountStatus')}
              </h3>
              <div className="space-y-4 relative z-10">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-brand-100 uppercase flex items-center gap-2">
                    <CheckCircle2 size={14} /> {t('profile.verification')}
                  </span>
                  <Badge variant="success" className="bg-white/20 border-none text-white">
                    {t('profile.verified')}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-brand-100 uppercase flex items-center gap-2">
                    <Shield size={14} /> {t('profile.role')}
                  </span>
                  <span className="text-sm font-black tracking-tight">
                    {translateRole(hookUser.role)}
                  </span>
                </div>
                {user.created_at && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-brand-100 uppercase flex items-center gap-2">
                      <Calendar size={14} /> {t('profile.memberSince')}
                    </span>
                    <span className="text-sm font-black tracking-tight">
                      {formatMemberSince(user.created_at, locale)}
                    </span>
                  </div>
                )}
              </div>
            </Card>

            {/* Privacy & Data Card */}
            <Card className="p-6">
              <h3 className="text-sm font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-6 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center">
                  <Lock size={16} className="text-text-subtle-light dark:text-text-subtle-dark" />
                </div>
                {t('profile.privacyData')}
              </h3>
              <div className="space-y-2">
                <DataActionBtn
                  icon={<Download size={16} />}
                  label={t('profile.exportData')}
                  onClick={() => navigate('/data-export')}
                />
                <DataActionBtn
                  icon={<FileText size={16} />}
                  label={t('profile.dpaAgreement')}
                  onClick={() => navigate('/dpa')}
                />
                <DataActionBtn
                  icon={<Shield size={16} />}
                  label={t('profile.manageConsents')}
                  onClick={() => navigate('/consents')}
                />
              </div>
            </Card>
          </div>
        </div>

        {/* Section 3: Danger Zone */}
        <Card className="p-8 border-state-danger-light/20 dark:border-state-danger-dark/20">
          <h2 className="text-xl font-black text-state-danger-light dark:text-state-danger-dark mb-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-state-danger-bg-light dark:bg-state-danger-bg-dark flex items-center justify-center">
              <AlertTriangle
                size={20}
                className="text-state-danger-light dark:text-state-danger-dark"
              />
            </div>
            {t('profile.dangerZone')}
          </h2>
          <p className="text-sm text-text-muted-light dark:text-text-muted-dark mb-6">
            {t('profile.deleteAccountWarning')}
          </p>
          <Button
            variant="outline"
            className="text-state-danger-light dark:text-state-danger-dark border-state-danger-light/20 dark:border-state-danger-dark/20 hover:bg-state-danger-bg-light dark:hover:bg-state-danger-bg-dark"
            onClick={() => navigate('/delete-account')}
          >
            <Trash2 size={16} className="mr-2" />
            {t('profile.deleteAccount')}
          </Button>
        </Card>
      </div>

      {/* Disable MFA Modal */}
      {showDisableMfa && (
        <div
          ref={mfaDialogRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="disable-mfa-title"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in"
        >
          <Card className="w-full max-w-md p-8 shadow-elevated">
            <h3
              id="disable-mfa-title"
              className="text-2xl font-black text-slate-900 dark:text-white mb-2"
            >
              {t('profile.mfa.disableModalTitle')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark mb-6 text-sm">
              {t('profile.mfa.disableModalDescription')}
            </p>
            <form onSubmit={handleDisableMFA} className="space-y-6">
              {error && (
                <p
                  role="alert"
                  className="rounded-lg bg-state-danger-bg-light p-3 text-state-danger-light dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
                >
                  {error}
                </p>
              )}
              <Input
                value={disableMfaCode}
                aria-label={t('profile.mfa.disableModalDescription')}
                disabled={disableMfaLoading}
                inputMode="numeric"
                pattern="[0-9]{6}"
                onChange={(e) => setDisableMfaCode(e.target.value.replace(/\D/g, ''))}
                maxLength={6}
                className="text-center text-2xl tracking-[0.5em] font-black"
                placeholder="000000"
                required
              />
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  type="button"
                  disabled={disableMfaLoading}
                  className="flex-1"
                  onClick={() => {
                    setError('');
                    setShowDisableMfa(false);
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  className="flex-1 bg-state-danger-light dark:bg-state-danger-dark hover:opacity-90 dark:text-on-accent-dark"
                  type="submit"
                  isLoading={disableMfaLoading}
                >
                  {t('common.confirm')}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </>
  );
}

function DataActionBtn({
  icon,
  label,
  onClick,
  color = 'text-slate-600 dark:text-slate-300',
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  color?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between p-3 rounded-xl hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors group ${color}`}
    >
      <div className="flex items-center gap-3">
        <span className="opacity-50 group-hover:opacity-100 transition-opacity">{icon}</span>
        <span className="text-sm font-bold">{label}</span>
      </div>
      <ChevronRight size={14} className="opacity-20 group-hover:opacity-100" />
    </button>
  );
}
